/**
 * Copy to clipboard, and say so.
 *
 * `navigator.clipboard` is unavailable on an insecure origin and can be refused by permission
 * policy, so a failure is reported rather than swallowed — on the one screen where this matters,
 * the token reveal, a copy that silently did nothing would let somebody close the dialog believing
 * they had the only copy of a credential they will never see again.
 */

import { useEffect, useRef, useState } from "react";

type Phase = "idle" | "copied" | "failed";

interface Props {
  value: string;
  className?: string;
  label?: string;
  onCopied?: () => void;
}

export function CopyButton({ value, className = "copy-button", label = "Copy", onCopied }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setPhase("copied");
      onCopied?.();
    } catch {
      setPhase("failed");
    }
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPhase("idle");
    }, 2000);
  }

  return (
    <button className={className} type="button" onClick={() => void copy()} data-phase={phase}>
      {phase === "copied" ? "Copied" : phase === "failed" ? "Press ⌘C" : label}
    </button>
  );
}
